import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography,
  Box,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import VenueForm from '../components/VenueForm';
import { logger } from "../utils/logger";

const EditVenuePage = () => {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchVenue = async () => {
      try {
        setLoading(true);
        const venueDoc = await getDoc(doc(db, 'venues', venueId));
        if (venueDoc.exists()) {
          const venueData = { id: venueDoc.id, ...venueDoc.data() };
          // Ensure opening_hours has all days initialized
          const defaultOpeningHours = {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: []
          };
          venueData.opening_hours = {
            ...defaultOpeningHours,
            ...venueData.opening_hours
          };
          logger.log('Fetched venue data:', venueData);
          setVenue(venueData);
        } else {
          throw new Error('Venue not found');
        }
      } catch (error) {
        console.error('Error fetching venue:', error);
        setAlert({
          open: true,
          message: 'Failed to load venue data',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchVenue();
  }, [venueId]);

  const handleSubmit = async (venueData) => {
    try {
      setSubmitting(true);
      
      const updateData = {
        ...venueData,
        updated_at: serverTimestamp()
      };
      
      // Remove the ID field if it exists
      if (updateData.id) {
        delete updateData.id;
      }
      
      // Ensure time slots are properly formatted as numbers
      if (updateData.opening_hours) {
        Object.keys(updateData.opening_hours).forEach(day => {
          updateData.opening_hours[day] = updateData.opening_hours[day].map(slot => ({
            start: typeof slot.start === 'string' ? parseInt(slot.start.replace(':', '')) : slot.start,
            end: typeof slot.end === 'string' ? parseInt(slot.end.replace(':', '')) : slot.end
          }));
        });
      }
      
      logger.log('Updating venue with data:', updateData);
      await updateDoc(doc(db, 'venues', venueId), updateData);
      
      setAlert({
        open: true,
        message: 'Venue updated successfully!',
        severity: 'success'
      });

      setTimeout(() => navigate('/admin'), 2000);
    } catch (error) {
      console.error('Error updating venue:', error);
      setAlert({
        open: true,
        message: 'Failed to update venue. Please try again.',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  const handleGooglePlaceSelected = (placeData) => {
    logger.log('Google place data received in edit mode:', placeData);
    setVenue(prev => ({
      ...prev,
      ...placeData,
      id: prev.id,
      photo_reference: placeData.photo_reference || prev.photo_reference
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {submitting && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 9999
        }}>
          <CircularProgress />
        </Box>
      )}
      
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Edit Venue
        </Typography>
        
        <VenueForm
          mode="edit"
          venue={venue}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onGooglePlaceSelected={handleGooglePlaceSelected}
          initialSearchQuery={venue?.headline} // Pass venue name as initial search query
        />
      </Paper>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditVenuePage;